import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { http, money } from "../lib/api";
import { useAuth } from "../lib/ctx";
import { toast } from "sonner";
import { NewsletterCTA } from "./Home";
import { MapPin, Phone, Mail, Instagram, Clock, Star } from "lucide-react";

/* ------- Auth ------- */
export function Login() {
  const { login } = useAuth(); const nav = useNavigate();
  const [f, setF] = useState({ email: "", password: "" }); const [busy, setBusy] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const u = await login(f.email, f.password);
      if (u.role === "customer") {
        toast.error("Staff access only. Customers can shop as guests.");
        localStorage.removeItem("oh_token");
        setBusy(false);
        return;
      }
      toast.success("Welcome back");
      nav("/admin");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Login failed");
    }
    setBusy(false);
  };
  return (
    <div className="max-w-md mx-auto px-4 py-20">
      <span className="oh-badge oh-badge-turq">Staff Only</span>
      <h1 className="font-serif text-5xl mb-2 mt-3">Staff Sign-In</h1>
      <p className="text-sm text-white/60 mb-6">This portal is for Organic Healing staff only. Customers can shop and check out as guests — no account required.</p>
      <form onSubmit={submit} className="oh-card p-8 space-y-4">
        <input required type="email" placeholder="Email" className="oh-input" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} data-testid="login-email"/>
        <input required type="password" placeholder="Password" className="oh-input" value={f.password} onChange={(e)=>setF({...f,password:e.target.value})} data-testid="login-password"/>
        <button disabled={busy} className="oh-btn-lime w-full py-3 rounded-full text-xs tracking-widest uppercase" data-testid="login-submit">{busy?"Signing in…":"Sign In"}</button>
        <div className="text-xs text-white/50 border-t border-[var(--oh-border)] pt-4 text-center">
          Track an order? <Link to="/track" className="text-lime-400 link-hover">Order Tracking →</Link>
        </div>
      </form>
    </div>
  );
}
export function Register() {
  // Retained as placeholder — customer registration has been disabled.
  return (
    <div className="max-w-md mx-auto px-4 py-20 text-center">
      <h1 className="font-serif text-5xl mb-4">No account needed</h1>
      <p className="text-white/70 mb-6">Shop and check out as a guest. To track an order, use your order number and email on the tracking page.</p>
      <div className="flex gap-3 justify-center">
        <Link to="/shop" className="oh-btn-lime px-6 py-2.5 rounded-full text-xs tracking-widest uppercase">Continue Shopping</Link>
        <Link to="/track" className="oh-btn-ghost px-6 py-2.5 rounded-full text-xs tracking-widest uppercase">Track My Order</Link>
      </div>
    </div>
  );
}

export function Account() {
  // Customer accounts have been disabled — redirect to order tracking.
  const nav = useNavigate();
  React.useEffect(() => { nav("/track", { replace: true }); }, [nav]);
  return null;
}

export function OrderTracking() {
  const [f, setF] = useState({ order_number: "", email: "" });
  const [order, setOrder] = useState(null); const [err, setErr] = useState("");
  const search = new URLSearchParams(window.location.search);
  useEffect(() => { const on = search.get("order"); if (on) setF((x)=>({...x, order_number: on})); }, []);
  const submit = async (e) => { e.preventDefault(); setErr(""); try { const { data } = await http.get(`/orders/track/${f.order_number}`, { params: { email: f.email } }); setOrder(data); } catch (er) { setErr(er.response?.data?.detail || "Not found"); } };
  return (
    <div className="max-w-3xl mx-auto px-4 py-14">
      <h1 className="font-serif text-5xl mb-6">Track Your Order</h1>
      <form onSubmit={submit} className="oh-card p-6 space-y-4 mb-8">
        <input required placeholder="Order number (e.g. OH-2026-00001)" className="oh-input" value={f.order_number} onChange={(e)=>setF({...f, order_number:e.target.value})} data-testid="track-order"/>
        <input type="email" placeholder="Email used at checkout (optional)" className="oh-input" value={f.email} onChange={(e)=>setF({...f, email:e.target.value})} data-testid="track-email"/>
        <button className="oh-btn-lime w-full py-3 rounded-full text-xs tracking-widest uppercase" data-testid="track-submit">Track</button>
      </form>
      {err && <div className="text-red-400 text-sm">{err}</div>}
      {order && (
        <div className="oh-card p-6 space-y-4">
          <div className="flex justify-between items-center"><div className="font-mono text-lime-400 text-2xl">{order.order_number}</div><span className="oh-badge oh-badge-lime">{order.status}</span></div>
          <div className="text-sm text-white/70">Fulfilment: <b className="capitalize">{order.fulfilment}</b> {order.tracking_number && <> · Tracking: <b>{order.tracking_number}</b> ({order.courier})</>}</div>
          <div className="border-t border-[var(--oh-border)] pt-4">{order.items.map((i,idx)=><div key={idx} className="flex justify-between text-sm"><span>{i.quantity} × {i.name}</span><span className="font-mono">{money(i.line_total)}</span></div>)}</div>
          <div className="border-t border-[var(--oh-border)] pt-4 flex justify-between font-mono"><span>Total</span><span className="text-lime-300">{money(order.total)}</span></div>
        </div>
      )}
    </div>
  );
}

/* ------- Static / content pages ------- */
export function About() {
  return (
    <div className="max-w-5xl mx-auto px-4 py-16">
      {/* Hero quote */}
      <div className="oh-card p-10 lg:p-14 rounded-3xl relative overflow-hidden mb-14">
        <div className="absolute -top-32 -right-32 w-96 h-96 rounded-full bg-gradient-to-br from-amber-500/20 via-lime-500/10 to-teal-500/20 blur-3xl"></div>
        <div className="relative">
          <span className="oh-badge oh-badge-gold">The Natural Path</span>
          <h1 className="font-serif text-4xl sm:text-6xl leading-tight mt-6 mb-6">
            "There emerges from their bellies a drink, varying in colours, in which there is <span className="italic text-amber-300">healing</span> for people."
          </h1>
          <p className="text-sm font-mono text-white/50 tracking-widest">— QUR'AN 16:69 · SURAH AN-NAHL (THE BEE)</p>
        </div>
      </div>

      {/* Story */}
      <div className="grid md:grid-cols-2 gap-10 mb-16">
        <div>
          <span className="oh-badge oh-badge-turq">Our Story</span>
          <h2 className="font-serif text-3xl mt-3 mb-4">Rooted in tradition. Made for today.</h2>
          <p className="text-white/75 leading-relaxed">
            Organic Healing was born from a simple frustration — how hard it had become to find honest, traceable natural products in the UK. Not endless marketing. Not exaggerated promises. Just clean ingredients, transparent labels and produce sourced with care.
          </p>
          <p className="text-white/75 leading-relaxed mt-4">
            We began with a few jars of raw honey from producers we trusted personally, and grew slowly from there — adding a supplement only when we could prove where every gram came from.
          </p>
        </div>
        <div>
          <span className="oh-badge oh-badge-lime">Our Philosophy</span>
          <h2 className="font-serif text-3xl mt-3 mb-4">Nature. Focus. Restoration.</h2>
          <p className="text-white/75 leading-relaxed">
            In an anxious, over-stimulated world, we believe wellness begins with quiet, deliberate rituals — a spoon of honey in warm water at dawn; ashwagandha stirred into milk at dusk; a moment of stillness before the day begins.
          </p>
          <p className="text-white/75 leading-relaxed mt-4">
            Our range is chosen to support the parts of life that feel most under pressure today: <b className="text-lime-400">focus, sleep, mental clarity and everyday restoration</b>.
          </p>
        </div>
      </div>

      {/* Prophetic tradition */}
      <div className="oh-card-honey rounded-2xl p-8 lg:p-12 mb-16">
        <span className="oh-badge oh-badge-gold">Tradition</span>
        <h2 className="font-serif text-3xl mt-4 mb-6">A tradition of natural goodness</h2>
        <p className="text-white/80 leading-relaxed max-w-3xl">
          The Prophet Muhammad ﷺ is reported to have said: <span className="italic text-amber-200">"Honey is a remedy for every illness and the Qur'an is a remedy for all illness of the mind, therefore I recommend to you both remedies, the Qur'an and honey."</span> — Sahih al-Bukhari.
        </p>
        <p className="text-white/70 leading-relaxed mt-4 max-w-3xl">
          For centuries, cultures across the world have looked to honey, black seed, olive oil, dates and pure resins as part of daily life. Organic Healing is our small contribution to keeping those traditions alive — sourced honestly, labelled truthfully, and delivered with respect for both the earth and the customer.
        </p>
      </div>

      {/* Range values */}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-16">
        {[
          { title: "Focus", body: "Gentle rituals that support daily concentration and calm attention." },
          { title: "Restoration", body: "Traditional foods designed to slow you down and refill the tank." },
          { title: "Mental Clarity", body: "Slow-crafted, single-origin ingredients for a clearer everyday." },
          { title: "Wholesome Sourcing", body: "Every batch traceable. Every ingredient honestly listed." },
        ].map((v, i) => (
          <div key={i} className="oh-card p-5" data-testid={`about-value-${i}`}>
            <div className="font-mono text-xs text-lime-400 tracking-widest">0{i + 1}</div>
            <h3 className="font-serif text-2xl mt-1 mb-2">{v.title}</h3>
            <p className="text-sm text-white/65">{v.body}</p>
          </div>
        ))}
      </div>

      {/* Standards / trust */}
      <div className="oh-card p-8 lg:p-10 rounded-3xl">
        <span className="oh-badge oh-badge-turq">Our Standards</span>
        <h2 className="font-serif text-3xl mt-3 mb-6">Honest by design</h2>
        <ul className="grid sm:grid-cols-2 gap-3 text-sm text-white/80">
          <li>✓ Registered UK <b>Food Hygiene</b> — clean handling, temperature-controlled storage</li>
          <li>✓ <b>Food Standards Agency</b> compliant labelling on every product</li>
          <li>✓ <b>Natural sourcing</b> with full country-of-origin and batch traceability (FEFO)</li>
          <li>✓ <b>Organic focus</b> where available — clearly marked when certified</li>
          <li>✓ Only <b>authorised UK nutrition claims</b> — never invented medical benefits</li>
          <li>✓ <b>Real reviews only</b> — verified customer purchases, no fabricated testimonials</li>
        </ul>
        <p className="text-xs text-white/40 mt-6 italic border-t border-[var(--oh-border)] pt-4">
          Food supplements should not be used as a substitute for a varied balanced diet. Not intended to diagnose, treat, prevent or cure any disease. If you are pregnant, breastfeeding, on medication or have a health condition, please consult a healthcare professional before use.
        </p>
      </div>
    </div>
  );
}

export function Contact() {
  const [f, setF] = React.useState({ name: "", email: "", message: "" });
  const [ok, setOk] = React.useState(false);
  const [info, setInfo] = React.useState(null);
  React.useEffect(() => { http.get("/contact-info").then((r) => setInfo(r.data)); }, []);
  const c = info || {};
  const waNum = (c.whatsapp || "+44 7877 855305").replace(/[^\d]/g, "");
  const mapEmbed = "https://www.google.com/maps?q=Unit%2013D%2C%2047%20Whitmore%20Rd%2C%20Small%20Heath%2C%20Birmingham%20B10%200NR&output=embed";
  return (
    <div className="max-w-6xl mx-auto px-4 py-14 grid lg:grid-cols-2 gap-10">
      <div className="space-y-6">
        <span className="oh-badge oh-badge-turq"><MapPin size={12} className="inline"/> Get in touch</span>
        <h1 className="font-serif text-5xl">Contact & Location</h1>
        <p className="text-white/70">We love hearing from customers. Message us via WhatsApp for the quickest reply, or drop us an email — we usually respond within one working day.</p>
        <div className="oh-card p-6 space-y-4 text-sm">
          <div className="flex gap-3 items-start text-white/90">
            <MapPin size={18} className="text-lime-400 mt-0.5"/>
            <div>
              <div>Unit 13D, 47 Whitmore Rd</div>
              <div>Small Heath, Birmingham</div>
              <div className="font-mono">B10 0NR</div>
            </div>
          </div>
          <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" data-testid="wa-link" className="flex gap-3 items-center text-white/90 link-hover">
            <Phone size={18} className="text-lime-400"/> <div><div className="font-mono">{c.phone}</div><div className="text-xs text-white/50">WhatsApp · Tap to message</div></div>
          </a>
          <a href={`mailto:${c.email}`} data-testid="email-link" className="flex gap-3 items-center text-white/90 link-hover">
            <Mail size={18} className="text-lime-400"/> <div><div className="font-mono">{c.email}</div><div className="text-xs text-white/50">Email · Reply within 1 working day</div></div>
          </a>
          <div className="flex gap-3 items-start text-white/90">
            <Clock size={18} className="text-lime-400 mt-0.5"/> <div><div>Monday – Saturday · 10:00 – 18:00</div><div className="text-xs text-white/50">Sunday closed</div></div>
          </div>
          <div className="flex gap-3 items-start text-white/90">
            <Instagram size={18} className="text-lime-400 mt-0.5"/> <a href="https://www.instagram.com/organichealinguk" target="_blank" rel="noreferrer" className="link-hover">@organichealinguk</a>
          </div>
        </div>
        <div className="oh-card p-6">
          <h3 className="font-serif text-2xl mb-4">Send a Message</h3>
          <form onSubmit={async (e)=>{e.preventDefault(); await http.post("/contact", f); setOk(true); setF({name:"",email:"",message:""});}} className="space-y-3">
            <input required placeholder="Name" className="oh-input" value={f.name} onChange={(e)=>setF({...f,name:e.target.value})} data-testid="contact-name"/>
            <input required type="email" placeholder="Email" className="oh-input" value={f.email} onChange={(e)=>setF({...f,email:e.target.value})} data-testid="contact-email"/>
            <textarea required rows={4} placeholder="How can we help?" className="oh-input" value={f.message} onChange={(e)=>setF({...f,message:e.target.value})} data-testid="contact-message"/>
            <button className="oh-btn-lime px-6 py-3 rounded-full text-xs tracking-widest uppercase w-full" data-testid="contact-submit">Send Message</button>
            {ok && <div className="text-lime-400 text-sm text-center">✓ Thank you — we'll be in touch shortly.</div>}
          </form>
        </div>
      </div>
      <div className="space-y-4">
        <span className="oh-badge oh-badge-gold"><MapPin size={12} className="inline"/> Visit us</span>
        <h2 className="font-serif text-3xl">Small Heath, Birmingham</h2>
        <p className="text-white/70 text-sm">Order online or collect from our shop — card and cash both accepted.</p>
        <div className="oh-card p-2 rounded-2xl overflow-hidden" data-testid="contact-map-wrap">
          <div className="aspect-square lg:aspect-[4/5] rounded-xl overflow-hidden border border-[var(--oh-border)]">
            <iframe title="Organic Healing Birmingham location"
              width="100%" height="100%" loading="lazy" allowFullScreen
              referrerPolicy="no-referrer-when-downgrade"
              src={mapEmbed}
              style={{border: 0}}
              data-testid="contact-map"/>
          </div>
        </div>
        <div className="flex gap-3">
          <a href="https://share.google/IdgRxjhYFkvqQnqoK" target="_blank" rel="noreferrer" data-testid="contact-directions" className="oh-btn-lime px-5 py-2.5 rounded-full text-xs tracking-widest uppercase inline-block">Open in Google Maps →</a>
          <a href={`https://wa.me/${waNum}`} target="_blank" rel="noreferrer" className="oh-btn-ghost px-5 py-2.5 rounded-full text-xs tracking-widest uppercase inline-block">WhatsApp Us</a>
        </div>
      </div>
    </div>
  );
}

export const DeliveryPage = () => <StaticContent title="Delivery & Collection" body={`We offer UK-wide delivery and in-store collection at our Birmingham shop.\n\nDELIVERY\n· Standard UK delivery from £4.95\n· Free UK delivery on orders over £40\n· Estimated delivery: 2 – 4 working days\n\nCOLLECTION · BIRMINGHAM\n· Unit 13D, 47 Whitmore Rd, Small Heath, Birmingham B10 0NR\n· Monday to Saturday · 10:00 – 18:00 · Sunday closed\n· Free — order online, we'll have it ready to collect\n· Card and cash both accepted in store\n· WhatsApp +44 7877 855305 when you arrive if you'd like us to bring it out\n\nAll orders are packed carefully with FEFO batch rotation to ensure freshness. You'll receive email updates as your order moves through Payment Pending → Paid → Processing → Ready for Collection / Dispatched → Delivered.`}/>;
export function Offers() {
  const [products, setProducts] = useState([]); const [promo, setPromo] = useState({percent:10, active:true});
  useEffect(()=>{ http.get("/products").then((r)=>setProducts(r.data.filter(p=>p.status==='active'))); http.get("/promo/current").then((r)=>setPromo(r.data));}, []);
  return (
    <div className="max-w-7xl mx-auto px-4 py-14">
      <span className="oh-badge oh-badge-gold">Live Offers</span>
      <h1 className="font-serif text-5xl mt-3 mb-2">{promo.active ? `${promo.percent}% Off Everything` : "Latest Offers"}</h1>
      <p className="text-white/70 mb-8">Discount applied automatically at checkout. Prices below already reflect your saving.</p>
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">{products.map((p) => <ProductCardImport key={p.id} product={p} promoPercent={promo.active?promo.percent:0}/>)}</div>
    </div>
  );
}
import ProductCardImport from "../components/ProductCard";

export function ReviewsPage() {
  const [reviews, setReviews] = useState([]);
  useEffect(()=>{ http.get("/reviews").then((r)=>setReviews(r.data)); }, []);
  return (
    <div className="max-w-5xl mx-auto px-4 py-14">
      <span className="oh-badge oh-badge-gold"><Star size={12} className="inline"/> Google Reviews</span>
      <h1 className="font-serif text-5xl mt-3 mb-3">Customer Reviews</h1>
      <p className="text-white/70 mb-8">Only genuine reviews are shown here. Real Google reviews will be imported and approved by our team — no fake reviews, ever.</p>
      <div className="mb-8 flex gap-3">
        <a href="https://share.google/LIRGdB96yARA7gH0T" target="_blank" rel="noreferrer" className="oh-btn-lime px-6 py-2.5 rounded-full text-xs tracking-widest uppercase">Read on Google</a>
        <a href="https://share.google/LIRGdB96yARA7gH0T" target="_blank" rel="noreferrer" className="oh-btn-ghost px-6 py-2.5 rounded-full text-xs tracking-widest uppercase">Leave a Review</a>
      </div>
      {reviews.length === 0 ? <div className="oh-card p-10 text-center text-white/60">Our first genuine reviews will be published here once approved.</div> : (
        <div className="grid md:grid-cols-2 gap-5">{reviews.map((r) => <div key={r.id} className="oh-card p-6"><div className="text-amber-400 mb-2">{'★'.repeat(r.rating)}{'☆'.repeat(5-r.rating)}</div><h3 className="font-serif text-2xl">{r.title}</h3><p className="text-sm text-white/70 mt-2">{r.body}</p><div className="text-xs mt-3 font-mono text-white/40">— {r.user_name}{r.verified_purchase && " · Verified purchase"}</div></div>)}</div>
      )}
    </div>
  );
}

export function StaticContent({ title, body }) {
  return (
    <div className="max-w-3xl mx-auto px-4 py-14">
      <h1 className="font-serif text-5xl mb-6">{title}</h1>
      <div className="oh-card p-8 whitespace-pre-line text-white/80 leading-relaxed">{body}</div>
    </div>
  );
}
export const FAQ = () => <StaticContent title="Frequently Asked Questions" body={`Q. Are your honey products raw?\nA. Every honey listed has its harvest region, net weight and ingredients on the product page.\n\nQ. Do you make medical claims?\nA. No. Only authorised UK nutrition and health claims are used. Traditional foods are described factually.\n\nQ. Can I collect my order in person?\nA. Yes — choose collection at checkout and pick a slot.\n\nQ. How is my data handled?\nA. Under UK GDPR. Marketing is opt-in only and you can unsubscribe at any time.\n\nQ. How are batches tracked?\nA. Every stock arrival is recorded with batch number and expiry. We use FEFO rotation.`}/>;
export const Privacy = () => <StaticContent title="Privacy Policy" body={`We handle your personal data under UK GDPR. We collect only what's needed to fulfil your order — name, email, address, phone (optional). Marketing emails are opt-in and you can unsubscribe at any time. We do not store card details; payments are processed securely by Stripe. Full policy will be maintained by the site owner.`}/>;
export const Terms = () => <StaticContent title="Terms & Conditions" body={`By using this website you agree to our terms of sale. Prices are shown in GBP and include applicable taxes where noted. Product images are illustrative — please refer to the product page for weight and packaging details. Full terms will be maintained by the site owner.`}/>;
export const Returns = () => <StaticContent title="Returns & Refunds" body={`Because our products are consumable food and supplements, returns are only accepted for unopened items within 14 days of delivery, or in the case of damage or mis-shipment. Please contact us before returning any item. Refunds are processed to the original payment method within 5–7 working days.`}/>;
export const DeliveryPolicy = () => <StaticContent title="Delivery Policy" body={`UK delivery from £4.95, free over £40. Estimated delivery 2–4 working days. Local collection available with slot booking. Delivery zones and blackout dates configured by the site owner.`}/>;
export const Cookies = () => <StaticContent title="Cookie Policy" body={`We use only essential cookies for cart, login and payment processing. No advertising cookies. You can clear cookies at any time via your browser settings.`}/>;

export { NewsletterCTA };
